import { apiClient } from '@shared/utils/api-client';
import type { Order, OrderStatusHistory } from '../types';

export interface UpdateOrderStatusDto {
  status: string;
  changedBy: 'ADMIN';
  comment?: string;
}

export interface RefundOrderDto {
  amount?: number;
  cancellationComment?: string;
}

export interface RefundOrderResponse {
  success: boolean;
  paymentStatus: string;
  orderCanceled: boolean;
}

export const ordersApi = {
  getAll: async (): Promise<Order[]> => {
    return apiClient.get<Order[]>('/orders');
  },

  getById: async (id: number): Promise<Order> => {
    return apiClient.get<Order>(`/orders/${id}`);
  },

  getByPickupPointId: async (pickupPointId: number, status?: string | null): Promise<Order[]> => {
    const query = status != null && status !== '' ? `?status=${encodeURIComponent(status)}` : '';
    return apiClient.get<Order[]>(`/orders/pickup-point/${pickupPointId}${query}`);
  },

  /** Пагинация для статусов DELIVERED/CANCELED: первые 50 по умолчанию, затем «загрузить ещё». Поиск по номеру/телефону/имени — на бэкенде. */
  getByPickupPointIdPage: async (
    pickupPointId: number,
    status: string,
    params: { limit?: number; offset?: number; search?: string },
  ): Promise<{ orders: Order[]; total: number }> => {
    const searchParams = new URLSearchParams();
    searchParams.set('status', status);
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.offset != null) searchParams.set('offset', String(params.offset));
    if (params.search != null && params.search.trim() !== '') {
      searchParams.set('search', params.search.trim());
    }
    return apiClient.get<{ orders: Order[]; total: number }>(
      `/orders/pickup-point/${pickupPointId}?${searchParams.toString()}`,
    );
  },

  updateStatus: async (id: number, dto: UpdateOrderStatusDto): Promise<Order> => {
    return apiClient.put<Order>(`/orders/${id}/status`, dto);
  },

  updateReady: async (id: number, files: File[], comment?: string): Promise<Order> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });
    if (comment) {
      formData.append('comment', comment);
    }
    formData.append('changedBy', 'ADMIN');

    // Используем axios напрямую для FormData
    const axiosInstance = (await import('@shared/utils/axios-instance')).default;
    const response = await axiosInstance.put<Order>(`/orders/${id}/ready`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  refund: async (id: number, dto?: RefundOrderDto): Promise<RefundOrderResponse> => {
    return apiClient.post<RefundOrderResponse>(`/orders/${id}/refund`, dto ?? {});
  },

  getStatusHistory: async (id: number): Promise<OrderStatusHistory[]> => {
    return apiClient.get<OrderStatusHistory[]>(`/orders/${id}/status-history`);
  },

  adminList: async (params: {
    statuses?: string[];
    cities?: string[];
    sellerIds?: number[];
    dateFrom?: string;
    dateTo?: string;
    delivery?: Array<'PICKUP' | 'DOSTAVISTA' | 'SELLER'>;
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<{ orders: Order[]; total: number }> => {
    const searchParams = new URLSearchParams();
    for (const status of params.statuses ?? []) searchParams.append('status', status);
    for (const city of params.cities ?? []) searchParams.append('city', city);
    for (const sellerId of params.sellerIds ?? []) searchParams.append('sellerId', String(sellerId));
    if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) searchParams.set('dateTo', params.dateTo);
    for (const delivery of params.delivery ?? []) searchParams.append('delivery', delivery);
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.offset != null) searchParams.set('offset', String(params.offset));
    if (params.search != null && params.search.trim() !== '') {
      searchParams.set('search', params.search.trim());
    }
    return apiClient.get<{ orders: Order[]; total: number }>(
      `/orders/admin/list?${searchParams.toString()}`,
    );
  },

  /** Поиск заказа по номеру или числовому id (админ). Ошибка 404, если не найден. */
  adminLookup: async (q: string): Promise<{ id: number; orderNumber: string }> => {
    const query = encodeURIComponent(q.trim());
    return apiClient.get<{ id: number; orderNumber: string }>(`/orders/admin/lookup?q=${query}`);
  },
};
